/**
 * THE PPI DESK. A pre-purchase inspection is not a repair, so it gets its
 * own steps, its own people and its own texts.
 *
 * Anthony, 2026-10-14: "the touchpoints, billing etc is a mess because the
 * process does not really match auto repair, as no auto repair is being
 * done." The numbers agreed with him. Of 60 PPI jobs from prepurchaseauto.com
 * the repair flow had 19 sitting at "estimate" and 27 cancelled. The buyer
 * got "here's the quote" and "on my way to you now" (the inspector meets the
 * SELLER, the buyer is usually in another city), and no seller was ever
 * texted once, because the repair flow has no seller in it.
 *
 * So the desk for the PrePurchase Auto account (accounts.desk_mode = 'ppi')
 * runs this file instead of quotes and invoices. The spec it follows is
 * _ops/ppi-desk/SPEC.md in the TodaysDavid tree.
 *
 * Pure, no imports, so the board, the server and the tests read one truth.
 */

import { firstNameOf } from "./greetname.ts";

/* ─────────────────────────────── the steps ─────────────────────────────── */

export const STEPS = [
  "new", "signed_paid", "scheduling", "scheduled", "inspecting", "review", "delivered", "closed",
] as const;
export const EXITS = ["lost", "missed", "refunded"] as const;
export type Step = typeof STEPS[number] | typeof EXITS[number];

export const STEP_LABEL: Record<Step, string> = {
  new: "New Request",
  signed_paid: "Signed & Paid",
  scheduling: "Scheduling",
  scheduled: "Scheduled",
  inspecting: "Inspecting",
  review: "Report Review",
  delivered: "Report Delivered",
  closed: "Closed",
  lost: "Lost",
  missed: "Missed",
  refunded: "Refunded",
};

/** What the buyer's tracker shows. Every step, in order, nothing internal. */
export const BUYER_STEPS: Step[] = [...STEPS];

/**
 * What the seller's tracker shows. The seller is not party to the booking
 * or the money, and never sees the report, so their list starts where they
 * come in and ends when the inspector leaves.
 */
export const SELLER_STEPS: Step[] = ["scheduling", "scheduled", "inspecting", "review"];
export const SELLER_LABEL: Partial<Record<Step, string>> = {
  scheduling: "Setting a Time", scheduled: "Scheduled", inspecting: "Inspecting", review: "Done",
};

export const isStep = (s: unknown): s is Step =>
  typeof s === "string" && ((STEPS as readonly string[]).includes(s) || (EXITS as readonly string[]).includes(s));

/* ─────────────────────────────── the people ─────────────────────────────── */

/**
 * Anthony, 2026-10-14: "they are all admin along with me. Eber is the only
 * dispatcher for now. We may make him supervisor in the future."
 *
 *   admin       Anthony, Nina, Alaina. Everything, money included.
 *   supervisor  Everything but pay. Nobody yet; Eber later.
 *   advisor     The DISPATCHER. Sees every request, works the seller and
 *               the inspector, approves a price. Cannot approve a report,
 *               mark a miss or refund.
 *   technician  The INSPECTOR. Never reads a request through this file's
 *               staff view at all; see inspectorView().
 */
export type Role = "admin" | "supervisor" | "advisor" | "technician" | "viewer";

export const ROLE_WORD: Record<Role, string> = {
  admin: "Admin", supervisor: "Supervisor", advisor: "Dispatcher", technician: "Inspector", viewer: "Viewer",
};

export const seesEverything = (r: string | null | undefined): boolean =>
  r === "admin" || r === "supervisor" || r === "advisor";
export const mayApprovePrice = seesEverything;
export const mayApproveReport = (r: string | null | undefined): boolean => r === "admin" || r === "supervisor";
export const mayRefund = mayApproveReport;

/* ─────────────────────────────── moving it ─────────────────────────────── */

/**
 * Which steps a person may move a request to by hand, from where it is.
 * The money step is not here: signed_paid is reached only by the buyer
 * signing and paying, never by a click, so nobody can "mark paid" a booking
 * that has no signature behind it.
 */
const BY_HAND: Partial<Record<Step, Step[]>> = {
  new: ["lost"],
  signed_paid: ["scheduling"],
  scheduling: ["scheduled", "missed"],
  scheduled: ["inspecting", "scheduling", "missed"],
  inspecting: ["review"],
  review: ["delivered", "inspecting"],
  delivered: ["closed"],
  missed: ["scheduled", "refunded"],
  lost: ["new"],
};

export function mayMove(role: string | null | undefined, from: Step, to: Step): { ok: true } | { ok: false; why: string } {
  if (!seesEverything(role)) return { ok: false, why: "Only the desk can move a request." };
  if (!(BY_HAND[from] ?? []).includes(to)) {
    return { ok: false, why: `A request in ${STEP_LABEL[from]} cannot go to ${STEP_LABEL[to]}.` };
  }
  if ((to === "delivered" || to === "refunded" || (from === "review" && to === "inspecting")) && !mayApproveReport(role)) {
    return { ok: false, why: "An admin or supervisor makes that call." };
  }
  return { ok: true };
}

/**
 * THE 24 HOUR PROMISE. Anthony: "after payment is taken we confirm
 * scheduling, usually within an hour or so, but as long as 24 hours is
 * possible. We will however contact you if there is any delay."
 */
export const DELAY_TEXT_AFTER_MIN = 120;
export const STAFF_WARN_AFTER_MIN = 20 * 60;
export const PROMISE_MIN = 24 * 60;
export const SELLER_SILENT_MIN = 45;
/** A miss is refunded only if nobody else gets out within this. */
export const MISS_REFUND_HOURS = 48;

/* ─────────────────────────────── who sees what ─────────────────────────── */

export type Request = {
  id: string; code: number; step: Step; created_at: string;
  source: string | null; from_brand: string | null;
  buyer_name: string | null; buyer_phone: string | null; buyer_email: string | null;
  vehicle_year: string | null; vehicle_make: string | null; vehicle_model: string | null; vehicle_type: string | null;
  vin: string | null; plate: string | null; plate_state: string | null; listing_url: string | null; notes: string | null;
  seller_kind: "dealer" | "private" | "unknown" | null; seller_name: string | null; seller_phone: string | null;
  seller_address: string | null; seller_city: string | null; seller_state: string | null; dealer_name: string | null;
  price: number | null; price_approved_at: string | null; paid_at: string | null;
  scheduled_for: string | null; inspector_id: string | null; dispatcher_id: string | null;
  step_at: string | null;
};

export const ppiCode = (n: number | null | undefined) => `P-${n ?? "?"}`;

export function vehicleOf(r: Pick<Request, "vehicle_year" | "vehicle_make" | "vehicle_model">): string {
  const v = [r.vehicle_year, r.vehicle_make, r.vehicle_model].map((x) => String(x ?? "").trim()).filter(Boolean).join(" ");
  return v || "the vehicle";
}

/**
 * THE INSPECTOR'S VIEW, built by naming what goes in rather than deleting
 * what must not. Anthony, 2026-10-09 and again 10-14: an inspector sees the
 * seller and their contact at Supercanic, "never the buyer. Never." A
 * strip-the-bad-keys approach leaks the next buyer column somebody adds;
 * a list of the keys that are allowed cannot.
 */
export type InspectorView = {
  id: string; code: string; step: Step; vehicle: string; vehicle_type: string | null;
  vin_last4: string | null; listing_url: string | null;
  seller_name: string | null; seller_phone: string | null; seller_address: string | null;
  seller_city: string | null; seller_state: string | null; dealer_name: string | null;
  scheduled_for: string | null;
  contact: { name: string; phone: string | null } | null;
};

export function inspectorView(r: Request, contact: { name: string; phone: string | null } | null): InspectorView {
  return {
    id: r.id, code: ppiCode(r.code), step: r.step, vehicle: vehicleOf(r), vehicle_type: r.vehicle_type,
    vin_last4: r.vin ? r.vin.slice(-4) : null, listing_url: r.listing_url,
    seller_name: r.seller_name, seller_phone: r.seller_phone, seller_address: r.seller_address,
    seller_city: r.seller_city, seller_state: r.seller_state, dealer_name: r.dealer_name,
    scheduled_for: r.scheduled_for, contact,
  };
}

/** The buyer's tracker. No seller phone or street, only the city. */
export function buyerView(r: Request, times: Partial<Record<Step, string>>) {
  return {
    who: "buyer" as const, code: ppiCode(r.code), step: r.step, vehicle: vehicleOf(r),
    city: [r.seller_city, r.seller_state].filter(Boolean).join(", ") || null,
    scheduled_for: r.scheduled_for, price: r.price_approved_at ? r.price : null, paid: Boolean(r.paid_at),
    steps: BUYER_STEPS.map((s) => ({ key: s, label: STEP_LABEL[s], at: times[s] ?? null })),
  };
}

/** The seller's tracker. No buyer, no price, no report. */
export function sellerView(r: Request, times: Partial<Record<Step, string>>, dealerPage: string | null) {
  return {
    who: "seller" as const, code: ppiCode(r.code), step: r.step, vehicle: vehicleOf(r),
    address: r.seller_address, scheduled_for: r.scheduled_for, dealer_page: dealerPage,
    steps: SELLER_STEPS.map((s) => ({ key: s, label: SELLER_LABEL[s] ?? STEP_LABEL[s], at: times[s] ?? null })),
  };
}

/* ─────────────────────────────── the texts ─────────────────────────────── */

export type Audience = "buyer" | "seller" | "inspector" | "staff";

/**
 * EVERY CUSTOMER TEXT CARRIES THE REFUND POLICY. Anthony, 2026-10-14:
 * "Refund Policy Link on every contact please." It is appended here, in
 * one place, rather than typed into each template where the twentieth one
 * would forget it. Inspector and staff texts are the shop talking to its
 * own people and do not carry it.
 */
export const REFUNDS_URL = "prepurchaseauto.com/refunds";

export type Fill = {
  first?: string; vehicle?: string; price?: string | number; tracker?: string; seller_tracker?: string;
  day?: string; time?: string; city?: string; address?: string; seller_first?: string; when?: string;
  inspector_first?: string; eta?: string | number; code?: string; payout?: string | number; day_window?: string;
  seller_name?: string; seller_phone?: string; dispatcher_name?: string; dispatcher_phone?: string;
  inspector_link?: string; payday?: string; note?: string; desk_link?: string; buyer_first?: string;
  step?: string; review?: string; book?: string; n?: string | number; from_brand?: string; dealer_page?: string;
  /** False when the request has no make or model yet (most call-ins), so the noCar wording goes instead. */
  car_known?: boolean;
  /** For the waiting alerts: minutes since the buyer's text, and what it said. */
  waited?: string | number; said?: string;
};

export type Touch = { key: string; audience: Audience; text: string };

const T: Record<string, { audience: Audience; text: string; noCar?: string }> = {
  /* THE HANDOFF. Anthony: "If the customer comes from Supercanic or
     Mechanicrank or another brand. Simply state that our nationwide
     company, which is us handles the process." One line, then B1. */
  H1: { audience: "buyer", text: "Hi {first}, {from_brand} here. Pre-purchase inspections are handled by our nationwide inspection company, PPI Auto, so they will take it from here." },

  /* THE CALL-IN WITH NO CAR YET. 35 of the 50 call-ins from 10-19 to 10-25
     had no make or model, and the quote read "your request for the car",
     then "Cars like the car sell fast" the next morning. Anthony, 10-26:
     fix the wording when we don't know the car. noCar is what goes when
     fillFor says car_known is false; same key, so the ledger is unchanged. */
  B1: { audience: "buyer", text: "Hi {first}, this is PPI Auto. We got your request for the {vehicle}. The inspection is ${price}: 122 points, photos and a written report, and we handle the seller for you. Book here: {tracker}",
    noCar: "Hi {first}, this is PPI Auto, thanks for reaching out. A pre-purchase inspection is ${price} for a car, pickup or SUV: 122 points, photos and a written report, and we handle the seller for you. Book here: {tracker} Or reply with the year, make and model and any questions." },
  B2: { audience: "buyer", text: "Hi {first}, still want the {vehicle} checked before you buy? Booking takes 2 minutes: {tracker}",
    noCar: "Hi {first}, still want the car checked before you buy? Booking takes 2 minutes: {tracker}" },
  B3: { audience: "buyer", text: "Cars like the {vehicle} sell fast. Book today and we reach out to the seller for you: {tracker}",
    noCar: "Good used cars sell fast. Book today and we reach out to the seller for you: {tracker}" },
  B4: { audience: "buyer", text: "Last note from us on the {vehicle}. If you went another way, no reply needed. Good luck with the search." },
  B5: { audience: "buyer", text: "Paid, thank you {first}. We are reaching the seller now. Most inspections are confirmed within an hour, and always within 24 hours. Track it here: {tracker}" },
  B6: { audience: "buyer", text: "Quick update on the {vehicle}: we are still working on a time with the seller. We will text the moment it is set. {tracker}" },
  B7: { audience: "buyer", text: "Set for {day} at {time} in {city}. Your inspector meets the seller there, so you don't need to go. {tracker}" },
  B8: { audience: "buyer", text: "The {vehicle} inspection moved to {day} at {time}. Nothing for you to do. {tracker}" },
  B9: { audience: "buyer", text: "Your inspector is with the {vehicle} now. The report usually lands within 2 hours. {tracker}" },
  B10: { audience: "buyer", text: "Your {vehicle} report is ready: {tracker} Want us to walk you through it? Reply CALL." },
  B11: { audience: "buyer", text: "Did you end up buying the {vehicle}? Reply YES or NO." },
  B13: { audience: "buyer", text: "We're sorry. We missed your {vehicle} inspection and couldn't get another inspector out within 48 hours, so your ${price} is refunded. It shows on your card in 3 to 5 business days." },

  S1: { audience: "seller", text: "Hi, this is PPI Auto. A buyer hired us to inspect your {vehicle} before they buy. It takes about an hour where the car is parked. What day and time work for you? Track it here: {seller_tracker}" },
  S3: { audience: "seller", text: "Thanks {seller_first}. Our inspector comes {day} at {time} to {address}. Please have the keys and title handy, and if you can, don't start the car before we get there. {seller_tracker}" },
  S3D: { audience: "seller", text: "Thanks {seller_first}. Our inspector comes {day} at {time} to {address}. Please have the keys and title handy, and if you can, don't start the car before we get there. {seller_tracker} Your dealer page: {dealer_page}" },
  S4: { audience: "seller", text: "Reminder: PPI Auto inspection of your {vehicle} {when} at {time}. Reply if anything changed. {seller_tracker}" },
  S5: { audience: "seller", text: "Your inspector {inspector_first} is on the way, about {eta} minutes out. {seller_tracker}" },

  I1: { audience: "inspector", text: "PPI job {code}: {vehicle} in {city}, {day_window}. Pays ${payout}. Reply YES to take it." },
  I2: { audience: "inspector", text: "It's yours. Seller: {seller_name}, {seller_phone}, {address}. {day} {time}. Your contact at Supercanic: {dispatcher_name} {dispatcher_phone}. Job: {inspector_link}" },
  I3: { audience: "inspector", text: "Today {time}: {vehicle} at {address}. Tap Start when you get there: {inspector_link}" },
  I4: { audience: "inspector", text: "Report on {code} approved. ${payout} goes out {payday}. Thank you." },
  I5: { audience: "inspector", text: "Report on {code} needs a fix: {note} {inspector_link}" },

  T1: { audience: "staff", text: "New PPI {code}: {vehicle}, {city}. Price needs approval. {desk_link}" },
  T2: { audience: "staff", text: "PPI paid {code}: {vehicle} in {city}. Seller texted. Clock is running. {desk_link}" },
  T3: { audience: "staff", text: "Call the seller on {code}: {seller_phone}" },
  T4: { audience: "staff", text: "{code} is close to the 24 hour promise. Text the buyer the reason now. {desk_link}" },
  T6: { audience: "staff", text: "Report on {code} is waiting for review. {desk_link}" },
  T7: { audience: "staff", text: "{code} missed. 48 hours to get someone out or refund. {desk_link}" },
  T8: { audience: "staff", text: "Call {buyer_first} about {code}. {desk_link}" },
  /* A BUYER WAITING ON US. 10-19 to 10-25, 29 buyers who never paid texted
     back, most with a question, and some waited 12 to 50 hours or got no
     reply at all. Anthony, 10-26: tell the staff when someone is waiting,
     by text, email and notification. W1 at 10 minutes, W2 at an hour. */
  W1: { audience: "staff", text: "{buyer_first} on {code} is waiting on a reply ({waited} min): \"{said}\" {desk_link}" },
  W2: { audience: "staff", text: "Still no reply to {buyer_first} on {code}, {waited} min now: \"{said}\" {desk_link}" },
};

export const TOUCH_KEYS = Object.keys(T);

/**
 * One touchpoint, filled. A slot with no value is an error rather than an
 * empty gap, because "Set for  at  in ." reaching a customer is worse than
 * the text not going: the caller logs it and a person sends it by hand.
 */
export function touch(key: string, f: Fill): Touch | { error: string } {
  const t = T[key];
  if (!t) return { error: `No touchpoint ${key}.` };
  const missing: string[] = [];
  const body = f.car_known === false && t.noCar ? t.noCar : t.text;
  let text = body.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = (f as Record<string, unknown>)[k];
    const s = v == null ? "" : String(v).trim();
    if (!s) missing.push(k);
    return s;
  });
  if (missing.length) return { error: `${key} is missing ${missing.join(", ")}.` };
  if (t.audience === "buyer" || t.audience === "seller") text += ` Refunds: ${REFUNDS_URL}`;
  return { key, audience: t.audience, text: text.replace(/\s{2,}/g, " ").trim() };
}

/** First name for a greeting, or nothing (the caller then skips the text). */
/* Through firstNameOf, so a caller-ID name ("WIRELESS CALLER", "PHOENIX
   AZ") is no name at all: 2026-10-16 the first auto quotes went out "Hi
   PHOENIX" and "Hi WIRELESS". */
export const firstName = (full: string | null | undefined) => firstNameOf(full);

/* ─────────────────────────────── the price ─────────────────────────────── */

/**
 * THE PRICE IS APPROVED BY A PERSON, FOR NOW. Anthony, 2026-10-14: the
 * prices "are on the website but subject to approval before sending for
 * now. We will modify later." The site says $225 for cars, pickups and SUVs
 * and "quoted" for the rest, so this suggests and a person confirms.
 */
export const STANDARD_PRICE = 225;
const QUOTED = /diesel|commercial|semi|rv|motorhome|trailer|motorcycle|powersport|atv|utv|classic|collector|exotic|luxury|equipment|forklift|tractor|boat|marine|generator/i;

export function suggestedPrice(vehicleType: string | null | undefined): { price: number | null; why: string } {
  const t = String(vehicleType ?? "").trim();
  if (!t || !QUOTED.test(t)) return { price: STANDARD_PRICE, why: "Car, pickup or SUV, the website's $225." };
  return { price: null, why: `The website says ${t} is quoted. Set the price.` };
}

/**
 * THE PRICE THAT APPROVES ITSELF. Anthony, 2026-10-16: "automatic ppi
 * quotes are fine. as long as it's a basic car inspection it's 225". So a
 * request for a car, pickup or SUV (or one where nobody said what it is
 * yet) gets $225 with no click. Anything the website quotes by hand, an
 * exotic make, or a car older than 1990 still waits for a person. Reads the
 * type, the make and model and the notes, with whole words only, so
 * "service" is not an RV.
 */
const NOT_BASIC = /\b(diesel|commercial|semi|rv|rvs|motor ?home|camper|trailer|motorcycles?|powersports?|atv|utv|classic|collector|exotic|equipment|forklift|tractor|boats?|marine|generator|bus|box truck|dually|ferrari|lamborghini|mclaren|rolls[- ]?royce|bentley|aston martin|maserati|lotus|bugatti|pagani|koenigsegg)\b/i;

export function autoPrice(r: Pick<Request, "vehicle_type" | "vehicle_year" | "vehicle_make" | "vehicle_model" | "notes">): number | null {
  const hay = [r.vehicle_type, r.vehicle_make, r.vehicle_model, r.notes].map((x) => String(x ?? "")).join(" | ");
  if (NOT_BASIC.test(hay)) return null;
  const year = Number(String(r.vehicle_year ?? "").trim());
  if (year && year < 1990) return null;
  return STANDARD_PRICE;
}

/**
 * WHERE A PPI REQUEST SITS ON THE SUPERCANIC LEADS BOARD (tasks/ppi-mirror.ts).
 * Waiting while nobody has booked it, Booked once it is paid, Converted
 * once the report is out, Lost when it is. "Contacted" rather than "new"
 * once somebody is working it, so the Supercanic board shows the same
 * "somebody has this" the PPI board does.
 */
export function mirrorStatus(step: Step, working: boolean): "new" | "contacted" | "booked" | "converted" | "lost" {
  if (step === "lost" || step === "refunded") return "lost";
  if (step === "delivered" || step === "closed") return "converted";
  if (step === "new") return working ? "contacted" : "new";
  return "booked";
}

/**
 * The search box as a PostgREST filter (the PPI board's, 2026-10-18).
 * Seven digits or more are a phone (the last 7, which dodges the +1);
 * "P-1047" is a code; a bare 3-6 digits is either; anything else is a name. The
 * text is cleaned to letters, digits and spaces, so it cannot reach the
 * filter grammar.
 */
export function searchFilter(raw: string | undefined): string | null {
  const t = String(raw ?? "").trim();
  if (!t) return null;
  const digits = t.replace(/\D/g, "");
  /* "P-1047" is a code. A bare "1047" could be the code or the last four
     of a phone, so it asks for both. */
  const code = t.match(/^p\s*-?\s*(\d{3,6})$/i)?.[1];
  if (code) return `code=eq.${Number(code)}`;
  if (/^[\d\s()+.-]+$/.test(t) && digits.length >= 3 && digits.length <= 6) {
    return `or=(code.eq.${Number(digits)},buyer_phone.like.*${digits}*)`;
  }
  if (digits.length >= 7) return `buyer_phone=like.*${digits.slice(-7)}*`;
  const name = t.replace(/[^\p{L}\p{N} ]/gu, " ").replace(/\s+/g, " ").trim().slice(0, 60);
  if (name.length < 2) return null;
  return `or=(buyer_name.ilike.*${encodeURIComponent(name)}*,seller_name.ilike.*${encodeURIComponent(name)}*,dealer_name.ilike.*${encodeURIComponent(name)}*)`;
}
